import { useEffect, useState, useMemo } from 'react'
import s from './Proposals.scss'
import { Container, Collapse, CardBody, Card, CardHeader, Button } from 'reactstrap'
import { AddButton } from 'components/common/AddButton'
import { getBaseUrl, makeGetRequest } from 'utils/api'
import { AgGridReact } from 'ag-grid-react'
import '!style-loader!css-loader!ag-grid-community/dist/styles/ag-grid.css' // eslint-disable-line
import '!style-loader!css-loader!ag-grid-community/dist/styles/ag-theme-balham.css' // eslint-disable-line
import '!style-loader!css-loader!styles/overrides/ag-grid.css' // eslint-disable-line
import { DraggableBoard } from 'components/common/DraggableBoard'
import { TableContainer } from 'components/ideal-core-ui'
import { currencyFormatter, timezoneDate, preventDefault } from 'utils/helpers'
import { groupProposalsByClient } from '../utils'
import { ProposalFileCellRenderer } from 'components/common/TableColumns/fileColumn/ProposalFileCellRenderer'
import { NameCellRenderer } from './nameColumn/NameCellRenderer'
import { CompanyCellRenderer } from 'components/common/TableColumns/companyColumn/CompanyCellRenderer'
import {
  FaAngleRight,
  FaAngleDown,
  FaPlusCircle,
  FaFileExport,
  FaCheckSquare,
  FaCertificate,
  FaExclamationCircle
} from 'react-icons/fa'
import {
  fromConstantPropertyToSelectValue,
  toProposalsListData,
  toProposalsBoardData,
  proposalTasksMapper
} from 'utils/serializers'
import { LaneHeader } from 'components/common/LaneHeader'
import { ProposalCard } from 'components/common/ProposalCard'
import { SwitchView } from 'components/common/SwitchView'
import { useSelector, useDispatch } from 'react-redux'
import { SendForInternalReviewModal } from 'components/common/modals/SendForInternalReviewModal'
import { PROPOSALS_SEND_FOR_REVIEW_REQUESTED, APPROVE_PROPOSALS_REQUESTED } from '../actions'
import { ApproveProposalsModal } from './ApproveProposalsModal'
import { AddProposalModalContainer } from 'components/common/modals/AddProposalModalContainer'
import swal from 'sweetalert'
import { getCompanyId } from '../helpers'
import { useParams } from 'react-router-dom'
import { useImmer } from 'use-immer'
import { CreateProposalFromTemplateModalContainer } from 'components/common/modals/CreateProposalFromTemplateModalContainer'
import cloneDeep from 'lodash/cloneDeep'
import concat from 'lodash/concat'
import { TasksModal } from 'components/common/modals/TasksModal'
import { toUsersSelect } from 'components/Users/serializers'
import { ScheduleTaskModal } from 'components/common/modals/ScheduleTaskModal'

const initialState = {
  collapse: '',
  showSendForInternalReviewModal: false,
  selectedProposals: [],
  proposalTasks: [],
  selectedProposal: null,
  showApproveProposalsModal: false,
  showAddProposalModal: false,
  showCreateProposalFromTemplateModal: false,
  showTasksModal: false,
  showScheduleTaskModal: false,
  task: null,
  editTasksModal: false
}

const ProposalsContainer = ({ dealCompanies, awardedCompany }) => {
  const { id: dealId } = useParams()
  const [state, setState] = useImmer(initialState)
  const [templateType, setTemplateType] = useState('')
  const [boardView, setBoardView] = useState(true)
  const [proposals, setProposals] = useState([])
  const users = useSelector(state => state.users)
  const { proposalsBoard } = useSelector(state => state.deal)
  const { dealsStatuses } = useSelector(state => state.deals)
  const dispatch = useDispatch()
  const [proposalsByClient, setProposalsByClient] = useState({})
  const [dealSummary, setDealSummary] = useState({})

  const boardData = useMemo(() => {
    return cloneDeep(toProposalsBoardData(proposalsBoard, dealsStatuses))
  }, [proposalsBoard, dealsStatuses])

  useEffect(() => {
    ;(async () => {
      const summary = await makeGetRequest(`/v1/deals/proposals-summary/${dealId}`)
      setDealSummary(summary)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setProposalsByClient(groupProposalsByClient(dealCompanies, proposals, 'name'))
  }, [awardedCompany, dealCompanies, proposals])

  useEffect(() => {
    getProposals()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!dealsStatuses?.length) {
      dispatch({
        type: 'DEAL_STATUSES_FETCH_REQUESTED'
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!users?.users?.length) {
      dispatch({
        type: 'USERS_FETCH_REQUESTED'
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getProposals = async params => {
    const response = await makeGetRequest(`/v1/proposals/deal?deal_id=${dealId}`)
    setProposals(toProposalsListData(users.users, response))

    if (params?.listOnly) return

    dispatch({
      type: 'PROPOSALS_DEAL_BOARD_DATA_FETCH_REQUESTED',
      payload: {
        dealId
      }
    })
  }

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'File',
        cellClass: 'file-column',
        cellRendererFramework: ProposalFileCellRenderer,
        cellRendererParams: {
          fileUrl: getBaseUrl('/v1/test/get-s3-object'),
          dealId,
          path: 'proposals'
        },
        suppressSizeToFit: true,
        resizable: false,
        enableRowGroup: false
      },
      {
        headerName: 'Current Version',
        field: 'version'
      },
      {
        headerName: 'Name',
        field: 'name',
        cellClass: 'deals-name-column',
        cellRendererFramework: NameCellRenderer,
        cellRendererParams: {
          path: 'proposals'
        }
      },
      {
        headerName: 'Due Date',
        field: 'dueDate',
        cellRenderer: data => {
          return data && data.value ? timezoneDate(data.value) : ''
        }
      },
      {
        headerName: 'Client',
        field: 'company',
        valueGetter: params => {
          if (!params.data || !params.data.company) return ''
          return params.data.company.name
        },
        cellRendererFramework: CompanyCellRenderer
      },
      {
        headerName: 'Status',
        field: 'status',
        cellClass: 'capitalize-column-content',
        valueFormatter: params => {
          return params && params.value
            ? fromConstantPropertyToSelectValue(params.value, 'ProposalStatus')?.label
            : ''
        }
      },
      {
        headerName: 'Assignee',
        valueGetter: params => {
          if (!params.data || !params.data.assignee) return ''
          const { firstName, lastName } = params.data.assignee
          return firstName + ' ' + lastName
        },
        cellClass: 'deals-name-column',
        cellRenderer: 'agGroupCellRenderer'
      },
      {
        headerName: 'Value',
        field: 'value',
        cellClass: 'center-align justify-content',
        valueFormatter: currencyFormatter
      },
      {
        headerName: 'Date Created',
        field: 'createdAt',
        cellRenderer: data => {
          return data && data.value ? timezoneDate(data.value) : ''
        }
      },
      {
        headerName: 'Created By',
        field: 'createdBy',
        cellClass: 'deals-name-column',
        cellRenderer: data => data?.value?.name
      },
      {
        headerName: 'Date Last Modified',
        field: 'updatedAt',
        cellRenderer: data => {
          return data && data.value ? timezoneDate(data.value) : ''
        }
      },
      {
        headerName: 'Modified By',
        field: 'updatedBy',
        cellClass: 'deals-name-column',
        cellRenderer: data => data?.value?.name
      }
    ],
    [dealId]
  )

  const toggle = e => {
    const event = e.target.dataset.event
    setState(draft => {
      draft.collapse = event
    })
  }

  const toggleSendForInternalReviewModal = () => {
    setState(draft => {
      draft.showSendForInternalReviewModal = !draft.showSendForInternalReviewModal
      draft.selectedProposals = proposals.map(p => p.id)
    })
  }

  const toggleApproveProposalsModal = () => {
    setState(draft => {
      draft.showApproveProposalsModal = !draft.showApproveProposalsModal
      draft.selectedProposals = proposals.map(p => p.id)
    })
  }

  const toggleSelectProposal = id => {
    const { selectedProposals } = state
    const index = selectedProposals.findIndex(p => p === id)
    if (~index) {
      const arr = [...selectedProposals]
      arr.splice(index, 1)
      setState(draft => {
        draft.selectedProposals = arr
      })
    } else {
      setState(draft => {
        draft.selectedProposals = [...selectedProposals, id]
      })
    }
  }

  const sendForInternalReview = async users => {
    dispatch({
      type: PROPOSALS_SEND_FOR_REVIEW_REQUESTED,
      payload: {
        proposalIds: state.selectedProposals,
        users,
        dealId
      }
    })

    toggleSendForInternalReviewModal()

    if (dealSummary.sendApprovalEmail === 'USER_ENABLED') {
      await swal({
        text: `Email to ${users.map(u => u.label).join(', ')} has been sent`
      })
    }
  }

  const approveProposals = () => {
    dispatch({
      type: APPROVE_PROPOSALS_REQUESTED,
      payload: {
        proposalIds: state.selectedProposals,
        dealId
      }
    })
    toggleApproveProposalsModal()
  }

  const toggleAddProposalModal = () => {
    setState(draft => {
      draft.showAddProposalModal = !draft.showAddProposalModal
    })
  }

  const toggleCreateProposalFromTemplateModal = () => {
    setState(draft => {
      draft.showCreateProposalFromTemplateModal = !draft.showCreateProposalFromTemplateModal
    })
  }

  const changeView = view => {
    setBoardView(view === 'board')
  }

  const changeStatus = (status, proposal) => {
    dispatch({
      type: 'DEAL_PROPOSAL_UPDATE_STATUS_REQUESTED',
      payload: {
        id: proposal.id,
        status,
        oldStatus: proposal.status,
        forDeal: true
      }
    })
  }

  const showTasksModal = (event, id, status) => {
    preventDefault(event)
    const data = boardData.lanes
    const category = data.find(d => d.id === status)
    if (category) {
      const selectedProposal = category.cards.filter(p => p.id === id)[0]
      const {
        completedTasks = [],
        lateTasks = [],
        futureTasks = [],
        todayTasks = []
      } = selectedProposal
      const proposalTasks = proposalTasksMapper(
        concat(completedTasks, lateTasks, futureTasks, todayTasks)
      )
      setState(draft => {
        draft.showTasksModal = !draft.showTasksModal
        draft.selectedProposal = selectedProposal
        draft.proposalTasks = proposalTasks
      })
    }
  }

  const toggleTasksModal = () => {
    setState(draft => {
      draft.showTasksModal = !draft.showTasksModal
    })
  }

  const showProposalScheduleTaskModal = (params = {}) => {
    const { selectedProposal } = state
    toggleTasksModal()
    toggleScheduleTaskModal(selectedProposal, selectedProposal.status, params)
  }

  const toggleScheduleTaskModal = (proposal, status, params = {}) => {
    setState(draft => {
      draft.showScheduleTaskModal = !draft.showScheduleTaskModal
      draft.editTasksModal = !!params.edit
      draft.task = params.task || null
      draft.selectedProposal = {
        ...proposal,
        status
      }
    })
  }

  const updateTask = task => {
    const { selectedProposal } = state
    dispatch({
      type: 'UPDATE_DEAL_BOARD_PROPOSAL_TASK_REQUESTED',
      payload: {
        task,
        proposalStatus: selectedProposal.status
      }
    })
  }

  const deleteTask = task => {
    dispatch({
      type: 'DELETE_DEAL_TASK_REQUESTED',
      payload: {
        task
      }
    })
  }

  const createTask = task => {
    const { selectedProposal } = state
    const { status, id } = selectedProposal
    dispatch({
      type: 'CREATE_DEAL_PROPOSAL_TASK_REQUESTED',
      payload: {
        task,
        proposalId: id,
        proposalStatus: status
      }
    })
  }

  const markTaskAsDone = (id, category) => {
    dispatch({
      type: 'DEAL_TASK_MARK_COMPLETED_REQUESTED',
      payload: {
        id,
        category,
        dealId
      }
    })
  }

  const markTaskAsNotDone = (id, category) => {
    dispatch({
      type: 'DEAL_TASK_MARK_NOT_COMPLETED_REQUESTED',
      payload: {
        id,
        category,
        dealId
      }
    })
  }

  return (
    <Container className={s.container}>
      <AddButton
        cy="upload_from_pdf_btn"
        className={s.createProposalFromTemplateBtn}
        onClick={toggleAddProposalModal}
      >
        <FaPlusCircle />
        Upload Proposal PDF
      </AddButton>
      <AddButton
        className={s.createProposalFromTemplateBtn}
        onClick={() => {
          setTemplateType('HTML')
          toggleCreateProposalFromTemplateModal()
        }}
      >
        <FaPlusCircle />
        Create Proposal from HTML
      </AddButton>
      <AddButton
        cy="upload_from_template_btn"
        className={s.createProposalFromTemplateBtn}
        onClick={() => {
          setTemplateType('WORD')
          toggleCreateProposalFromTemplateModal()
        }}
      >
        <FaPlusCircle />
        Create Proposal from Word
      </AddButton>
      {proposals.length > 0 && (
        <>
          <Button className={s.sendProposalForReviewBtn} onClick={toggleSendForInternalReviewModal}>
            <FaFileExport />
            Send Proposal(s) for Internal Review
          </Button>
          <Button className={s.approveButton} onClick={toggleApproveProposalsModal}>
            <FaCheckSquare />
            Approve Proposals
          </Button>
        </>
      )}
      <div className={s.titleWrapper}>
        <div className={s.proposalsInfo}>
          <div className={s.exclamationMark}>
            <FaExclamationCircle />
          </div>
          <span>
            Each card below represents a proposal created for this opportunity. Proposal status
            drives opportunity status automatically. The further to the right in the pipeline a
            proposal is – the closer you are to closing an opportunity. When you have multiple
            proposals, the proposal furthest to the right sets the status of the opportunity as
            shown above each column.
          </span>
        </div>
        <div className={s.switchViewContainer}>
          <SwitchView isBoardView={boardView} changeView={changeView} />
        </div>
      </div>
      {boardView ? (
        <div className={s.boardWrapper}>
          <DraggableBoard
            onDragEnd={changeStatus}
            data={boardData}
            customLaneHeader={<LaneHeader dataType="proposal" />}
          >
            <ProposalCard showTasksModal={showTasksModal} />
          </DraggableBoard>
        </div>
      ) : (
        <TableContainer className={s.proposalsTable} selectable>
          {gridData => <AgGridReact {...gridData} columnDefs={columnDefs} rowData={proposals} />}
        </TableContainer>
      )}
      {Object.keys(proposalsByClient).map(proposal => {
        const data =
          typeof proposalsByClient[proposal] !== 'string' ? proposalsByClient[proposal] : []
        const companyId = getCompanyId(proposalsByClient, proposal)
        return (
          <Card className={s.card} key={proposal}>
            <CardHeader onClick={toggle} data-event={proposal}>
              <span className={s.arrow}>
                {state.collapse === proposal ? <FaAngleRight /> : <FaAngleDown />}
              </span>
              {awardedCompany?.value?.toString() === companyId?.toString() && (
                <FaCertificate className={s.awardIcon} title="Opportunity Awarded" />
              )}
              {proposal} - Proposals
            </CardHeader>
            <Collapse isOpen={state.collapse === proposal}>
              <CardBody>
                {state.collapse === proposal && (
                  <>
                    <p className={s.title}>Proposal(s)</p>
                    <TableContainer className={s.proposalsTable} selectable>
                      {gridData => (
                        <AgGridReact {...gridData} columnDefs={columnDefs} rowData={data} />
                      )}
                    </TableContainer>
                  </>
                )}
              </CardBody>
            </Collapse>
          </Card>
        )
      })}
      {state.showApproveProposalsModal && (
        <ApproveProposalsModal
          showApproveProposalsModal={state.showApproveProposalsModal}
          toggleApproveProposalsModal={toggleApproveProposalsModal}
          proposals={proposals}
          toggleSelectProposal={toggleSelectProposal}
          size="md"
          selectedProposals={state.selectedProposals}
          approveProposals={approveProposals}
        />
      )}
      {state.showSendForInternalReviewModal && (
        <SendForInternalReviewModal
          showSendForInternalReviewModal={state.showSendForInternalReviewModal}
          toggleSendForInternalReviewModal={toggleSendForInternalReviewModal}
          sendForInternalReview={sendForInternalReview}
          proposals={proposals}
          toggleSelectProposal={toggleSelectProposal}
          size="lg"
          selectedProposals={state.selectedProposals}
        />
      )}
      {state.showAddProposalModal && (
        <AddProposalModalContainer
          showAddProposalModal={state.showAddProposalModal}
          toggleAddProposalModal={toggleAddProposalModal}
          deal={{ value: dealSummary.id, label: dealSummary.name }}
          createProposalCb={() => getProposals({ listOnly: true })}
        />
      )}
      {state.showCreateProposalFromTemplateModal && (
        <CreateProposalFromTemplateModalContainer
          toggleCreateProposalFromTemplateModal={toggleCreateProposalFromTemplateModal}
          showCreateProposalFromTemplateModal={state.showCreateProposalFromTemplateModal}
          dealId={dealId}
          dealCompanies={dealCompanies}
          proposalsByClient={proposalsByClient}
          dealValue={dealSummary.dealValue}
          templateType={templateType}
          createProposalCb={() => getProposals({ listOnly: true })}
        />
      )}
      {state.showTasksModal && (
        <TasksModal
          title={state.selectedProposal && `Active Tasks for ${state.selectedProposal.name}`}
          showScheduleTaskModal={showProposalScheduleTaskModal}
          showTasksModal={showTasksModal}
          toggleTasksModal={toggleTasksModal}
          tasks={state.proposalTasks}
          markAsDone={markTaskAsDone}
          markAsNotDone={markTaskAsNotDone}
        />
      )}
      {state.showScheduleTaskModal && (
        <ScheduleTaskModal
          showScheduleTaskModal={state.showScheduleTaskModal}
          toggleScheduleTaskModal={toggleScheduleTaskModal}
          createTask={createTask}
          updateTask={updateTask}
          users={toUsersSelect(users.users)}
          proposal={state.selectedProposal}
          edit={state.editTasksModal}
          selectedTask={state.task}
          markAsDone={markTaskAsDone}
          markAsNotDone={markTaskAsNotDone}
          deleteTask={deleteTask}
          proposals={proposals}
        />
      )}
    </Container>
  )
}

export default ProposalsContainer
